import { notification } from 'antd';
import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import { uploadFile } from './upload';
import { getValidToken, keycloak } from '../auth/keycloak';

export const isNetworkError = (error: any) => {
    return error.code === 'ERR_NETWORK';
};

axios.interceptors.response.use();

export class ApiInstance {
    private static instance: AxiosInstance;
    private static token: string;

    private constructor() {}

    public static getInstance() {
        if (!this.instance) {
            // Source of truth for the bearer token is the Keycloak singleton
            // (keycloak.token is the in-memory access token, refreshed via
            // getValidToken(30) before each request in the interceptor below).
            const initialToken = keycloak.token ?? this.token;
            const headers: AxiosRequestHeaders = {
                'Content-Type': 'application/json',
            } as AxiosRequestHeaders;
            if (initialToken !== undefined) {
                headers.Authorization = `Bearer ${initialToken}`;
            }

            this.instance = axios.create({
                baseURL: process.env.REACT_APP_API_BASE_URL,
                headers,
                withCredentials: false,
            });

            this.instance.interceptors.request.use(
                async config => {
                    if (config.headers) {
                        // Force a token refresh if the access token expires
                        // within 30s. Returns null if not authenticated.
                        const token = await getValidToken(30);
                        if (token) {
                            config.headers['Authorization'] = `Bearer ${token}`;
                        }
                    }
                    return config;
                },
                error => {
                    return Promise.reject(error);
                }
            );

            this.instance.interceptors.response.use(
                function (response) {
                    return response;
                },
                function (error) {
                    if (isNetworkError(error)) {
                        notification.error({
                            message: 'Network Error',
                            description: 'Please check your network connection',
                        });
                        return Promise.reject('error');
                    }
                    if (error.response?.status === 401) {
                        localStorage.removeItem('userInfo');
                        // The AuthGuard will normally intercept unauthenticated
                        // access before a 401 happens, but if a token is
                        // rejected (e.g. revoked server-side) we force a
                        // re-login via Keycloak.
                        void keycloak.login({
                            redirectUri: window.location.href,
                        });
                    }
                    return Promise.reject(error);
                }
            );
        }
        return this.instance;
    }

    /**
     * Kept for API compatibility with existing callers. The Keycloak
     * singleton owns the token now; this just makes sure the next request
     * picks it up. No localStorage is touched.
     */
    public static setToken(token: string) {
        if (this.instance && token) {
            this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
        }
        this.token = token;
    }

    public static resetToken() {
        if (this.instance) {
            delete this.instance.defaults.headers.common['Authorization'];
        }
        this.token = '';
    }

    public static async uploadImages(
        licensePlate: string,
        file: File,
        onProgress: (progress: {
            loaded: number;
            total: number;
            percentage: number;
        }) => void
    ) {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/images/upload/${licensePlate}`;
        return uploadFile(url, file, onProgress);
    }

    public static async uploadCarImages(
        carId: string | number,
        file: File,
        onProgress: (progress: {
            loaded: number;
            total: number;
            percentage: number;
        }) => void
    ) {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/carImages/upload/${carId}`;
        return uploadFile(url, file, onProgress);
    }
}
